import { DefaultCashBasisAccountCodes } from "@common/constants/domains/accounting/accounts";
import { AccountRole } from "@common/types/domains/accounting/account";

// The placeholder "no decision yet" income/expense accounts. Letting one of
// these win a vote or be copied forward feeds back into itself and permanently
// pins a cluster on the placeholder, so every reference-copy / normalization
// vote treats them as the absence of an account decision, not a decision.
export const UNCATEGORIZED_ACCOUNT_ROLES: readonly AccountRole[] = [
    AccountRole.uncategorizedIncome,
    AccountRole.uncategorizedExpense,
];

export const UNCATEGORIZED_KICK_ORIGINAL_CODES: readonly number[] = [
    DefaultCashBasisAccountCodes.uncategorizedIncome,
    DefaultCashBasisAccountCodes.uncategorizedExpenses,
];

// Name patterns (ILIKE, matched against the trimmed name) for placeholder
// accounts imported from external systems without a role: QuickBooks'
// "Uncategorized Expense" / "Uncategorized", "Ask My Accountant", "Ask The
// Client" / "Ask Client", and accounts QBO renamed with a "(deleted)" marker
// on deletion.
export const PLACEHOLDER_ACCOUNT_NAME_PATTERNS: readonly string[] = [
    "uncategorized%",
    "ask my accountant",
    "ask the client",
    "ask client",
    "%(deleted)%",
];

const PLACEHOLDER_ACCOUNT_NAME_REGEXES: readonly RegExp[] =
    PLACEHOLDER_ACCOUNT_NAME_PATTERNS.map(
        (pattern) =>
            new RegExp(
                `^${pattern
                    .split("%")
                    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                    .join(".*")}$`,
                "i",
            ),
    );

/**
 * Whether an account name matches {@link PLACEHOLDER_ACCOUNT_NAME_PATTERNS}.
 * Mirrors the name branch of {@link buildPlaceholderAccountCondition} in TS.
 */
export function isPlaceholderAccountName(name: string): boolean {
    const trimmedName = name.trim();
    return PLACEHOLDER_ACCOUNT_NAME_REGEXES.some((regex) =>
        regex.test(trimmedName),
    );
}

export const KICK_INTERCOMPANY_ACCOUNT_ROLES: readonly AccountRole[] = [
    AccountRole.intercompanyReceivable,
    AccountRole.intercompanyPayable,
];

export const KICK_INTERCOMPANY_KICK_ORIGINAL_CODES: readonly number[] = [
    DefaultCashBasisAccountCodes.intercompanyReceivables, // 190000
    DefaultCashBasisAccountCodes.intercompanyPayables, // 290000
];

export interface UncategorizedAccountPredicateInput {
    role: AccountRole | null;
    kickOriginalCode: number | null;
}

export interface KickIntercompanyAccountPredicateInput {
    role: AccountRole | null;
    kickOriginalCode: number | null;
    relatedEntityId: number | null;
}

/**
 * Whether an account is a Kick-sourced intercompany account: either a pair
 * account ("Due to/from X") provisioned by `FlipAccountProvisioningService`
 * (`relatedEntityId` set), or one of the generic intercompany accounts Kick
 * ships with every chart (`role` / `kickOriginalCode`). User-created "Due
 * to/from" accounts have none of these signals and are NOT Kick-sourced, so
 * they stay eligible for AI similarity rules. Intercompany coding must come
 * from transfer matching, never from a description-similarity rule.
 */
export function isKickIntercompanyAccount({
    role,
    kickOriginalCode,
    relatedEntityId,
}: KickIntercompanyAccountPredicateInput): boolean {
    if (relatedEntityId !== null) {
        return true;
    }
    if (role !== null && KICK_INTERCOMPANY_ACCOUNT_ROLES.includes(role)) {
        return true;
    }
    return (
        kickOriginalCode !== null &&
        KICK_INTERCOMPANY_KICK_ORIGINAL_CODES.includes(kickOriginalCode)
    );
}

/**
 * Whether an account is one of the uncategorized income/expense placeholders.
 *
 * `role` is the primary signal, but it is nullable: the CoA template writer
 * nulls it when the role slot is already occupied, and manually-created /
 * QBD-imported accounts have none. Fall back to the Kick native original code
 * (499099 / 799099) so a role-less placeholder is still recognised.
 */
export function isUncategorizedAccount({
    role,
    kickOriginalCode,
}: UncategorizedAccountPredicateInput): boolean {
    if (role !== null && UNCATEGORIZED_ACCOUNT_ROLES.includes(role)) {
        return true;
    }
    return (
        kickOriginalCode !== null &&
        UNCATEGORIZED_KICK_ORIGINAL_CODES.includes(kickOriginalCode)
    );
}

export interface PlaceholderAccountCondition {
    condition: string;
    parameters: {
        placeholderNamePatterns: string[];
        placeholderRoles: readonly AccountRole[];
        placeholderKickCodes: readonly number[];
    };
}

/**
 * SQL condition that is true when the account is a "no decision yet"
 * placeholder: matched by name ({@link PLACEHOLDER_ACCOUNT_NAME_PATTERNS}) for
 * role-less accounts imported from external systems, or by the uncategorized
 * `role` / `kickOriginalCode` for Kick's own placeholders, even when renamed.
 * Negate it with `NOT (...)` to keep only real accounts. Quoted like
 * {@link buildVotableAccountJoinCondition} so TypeORM leaves the alias alone.
 */
export function buildPlaceholderAccountCondition(
    accountAlias: string,
): PlaceholderAccountCondition {
    const name = `"${accountAlias}"."name"`;
    const role = `"${accountAlias}"."role"`;
    const kickCode = `"${accountAlias}"."kickOriginalCode"`;
    return {
        condition:
            `(TRIM(${name}) ILIKE ANY (:placeholderNamePatterns) ` +
            `OR COALESCE(${role} IN (:...placeholderRoles), false) ` +
            `OR COALESCE(${kickCode} IN (:...placeholderKickCodes), false))`,
        parameters: {
            placeholderNamePatterns: [...PLACEHOLDER_ACCOUNT_NAME_PATTERNS],
            placeholderRoles: UNCATEGORIZED_ACCOUNT_ROLES,
            placeholderKickCodes: UNCATEGORIZED_KICK_ORIGINAL_CODES,
        },
    };
}

export interface PlaceholderAccountPredicateInput extends UncategorizedAccountPredicateInput {
    name: string;
}

/**
 * Whether an account is a "no decision yet" placeholder: Kick's uncategorized
 * account ({@link isUncategorizedAccount}) or a role-less placeholder imported
 * from an external system ({@link isPlaceholderAccountName}). Mirrors
 * {@link buildPlaceholderAccountCondition} in TS.
 */
export function isPlaceholderAccount({
    name,
    role,
    kickOriginalCode,
}: PlaceholderAccountPredicateInput): boolean {
    return (
        isUncategorizedAccount({ role, kickOriginalCode }) ||
        isPlaceholderAccountName(name)
    );
}

export interface VotableAccountJoinCondition {
    condition: string;
    parameters: {
        unvotableRoles: readonly AccountRole[];
        unvotableKickCodes: readonly number[];
        unvotableNamePatterns: string[];
    };
}

/**
 * SQL join condition (for a LEFT JOIN onto the account table) that resolves an
 * account id only when the account is votable, i.e. NOT a placeholder AND NOT
 * a Kick-sourced intercompany account. Mirrors `isPlaceholderAccount` /
 * `isKickIntercompanyAccount` in SQL: an account is unvotable when its `role`
 * is one of the placeholder/intercompany roles OR (for null-role placeholders
 * such as QBD-imported / gl-first accounts) its `kickOriginalCode` is 499099 /
 * 799099 / 190000 / 290000 OR its trimmed name matches
 * {@link PLACEHOLDER_ACCOUNT_NAME_PATTERNS} (role-less QuickBooks "Ask My
 * Accountant" / "Uncategorized Expense") OR it is a pair account
 * (`relatedEntityId` set). Checking `role` alone lets a null-role placeholder
 * survive the join and win a false consensus, so every signal must be
 * excluded here.
 *
 * Excluded accounts resolve to `NULL` and land in the "no decision" bucket, so
 * they still count in the denominator (existing contract, unchanged) — an
 * intercompany account can no longer win a majority vote, but transfers under
 * the same head are not otherwise re-weighted.
 *
 * `accountAlias` is the account query-builder alias (e.g. `acct`). Both the
 * alias and the columns are quoted (`"acct"."role"`) so the fragment is a
 * literal that matches TypeORM's always-quoted emitted alias regardless of
 * alias casing — a bare `alias.column` token would be rewritten by TypeORM's
 * property-name replacement, but a quoted column leaves the alias untouched and
 * a camelCase alias would then leak unquoted.
 */
export function buildVotableAccountJoinCondition(
    accountAlias: string,
): VotableAccountJoinCondition {
    const role = `"${accountAlias}"."role"`;
    const kickCode = `"${accountAlias}"."kickOriginalCode"`;
    const relatedEntityId = `"${accountAlias}"."relatedEntityId"`;
    const name = `"${accountAlias}"."name"`;
    return {
        condition:
            `(${role} IS NULL OR ${role} NOT IN (:...unvotableRoles)) ` +
            `AND (${kickCode} IS NULL OR ${kickCode} NOT IN (:...unvotableKickCodes)) ` +
            `AND NOT COALESCE(TRIM(${name}) ILIKE ANY (:unvotableNamePatterns), false) ` +
            `AND ${relatedEntityId} IS NULL`,
        parameters: {
            unvotableNamePatterns: [...PLACEHOLDER_ACCOUNT_NAME_PATTERNS],
            unvotableRoles: [
                ...UNCATEGORIZED_ACCOUNT_ROLES,
                ...KICK_INTERCOMPANY_ACCOUNT_ROLES,
            ],
            unvotableKickCodes: [
                ...UNCATEGORIZED_KICK_ORIGINAL_CODES,
                ...KICK_INTERCOMPANY_KICK_ORIGINAL_CODES,
            ],
        },
    };
}
